/**
 * Shared contract and validation for the Sentience pilot request form.
 *
 * Imported by the browser form and by the Cloudflare Pages Function behind
 * `POST /api/pilot-interest` on odingard.com, so one set of rules applies on
 * both sides and the server never trusts the client to have applied them. Free of framework and provider
 * dependencies on purpose: it has to run in the browser and in a Worker.
 */

export type PilotRequest = {
  fullName: string;
  workEmail: string;
  organization: string;
  role: string;
  entityScale: string;
  jurisdictions: string;
  complianceToday: string;
  timing: string;
  notes?: string;
  consent: true;
  /** Honeypot. Must be empty; a browser never sees the field. */
  website?: string;
};

export const ENTITY_SCALES = [
  '1 legal entity',
  '2\u20135 entities',
  '6\u201325 entities',
  '26\u2013100 entities',
  '100+ entities',
] as const;

export const JURISDICTION_RANGES = [
  '1\u20133 states',
  '4\u201310 states',
  '11\u201325 states',
  '26\u201350 states',
  'All 50 states and DC',
] as const;

export const COMPLIANCE_TODAY = [
  'Spreadsheets and calendars',
  'In-house compliance team',
  'Outside counsel or a filing agent',
  'A GRC platform',
  'Nothing systematic yet',
] as const;

export const TIMINGS = ['Within 30 days', '30\u201390 days', '90+ days', 'Exploring'] as const;

export const CONSENT_TEXT =
  'I agree that the Sentience team may contact me about the pilot program.';

const MAX = {
  fullName: 120,
  workEmail: 254,
  organization: 160,
  role: 120,
  notes: 2000,
} as const;

/** Deliberately conservative: one @, no spaces, a dotted domain. */
const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

export type FieldName =
  | 'fullName'
  | 'workEmail'
  | 'organization'
  | 'role'
  | 'entityScale'
  | 'jurisdictions'
  | 'complianceToday'
  | 'timing'
  | 'notes'
  | 'consent';

export type ValidationErrors = Partial<Record<FieldName, string>>;

export type ValidationResult =
  | { ok: true; value: PilotRequest; honeypot: false }
  | { ok: false; errors: ValidationErrors; honeypot: boolean };

function str(input: unknown): string {
  return typeof input === 'string' ? input.trim().replace(/\s+/g, ' ') : '';
}

function multiline(input: unknown): string {
  return typeof input === 'string' ? input.trim() : '';
}

export function validatePilotRequest(input: unknown): ValidationResult {
  const raw = (input ?? {}) as Record<string, unknown>;
  const errors: ValidationErrors = {};

  const honeypot = str(raw.website).length > 0;

  const fullName = str(raw.fullName);
  const workEmail = str(raw.workEmail).toLowerCase();
  const organization = str(raw.organization);
  const role = str(raw.role);
  const entityScale = str(raw.entityScale);
  const jurisdictions = str(raw.jurisdictions);
  const complianceToday = str(raw.complianceToday);
  const timing = str(raw.timing);
  const notes = multiline(raw.notes);
  const consent = raw.consent === true || raw.consent === 'true' || raw.consent === 'on';

  if (!fullName) errors.fullName = 'Enter your full name.';
  else if (fullName.length > MAX.fullName) errors.fullName = 'Name is too long.';

  if (!workEmail) errors.workEmail = 'Enter your work email address.';
  else if (workEmail.length > MAX.workEmail || !EMAIL_RE.test(workEmail))
    errors.workEmail = 'Enter a valid work email address.';

  if (!organization) errors.organization = 'Enter your organization.';
  else if (organization.length > MAX.organization) errors.organization = 'Organization is too long.';

  if (!role) errors.role = 'Enter your role or title.';
  else if (role.length > MAX.role) errors.role = 'Role is too long.';

  if (!ENTITY_SCALES.includes(entityScale as (typeof ENTITY_SCALES)[number]))
    errors.entityScale = 'Select how many legal entities you file for.';

  if (!JURISDICTION_RANGES.includes(jurisdictions as (typeof JURISDICTION_RANGES)[number]))
    errors.jurisdictions = 'Select the jurisdictions you need watched.';

  if (!COMPLIANCE_TODAY.includes(complianceToday as (typeof COMPLIANCE_TODAY)[number]))
    errors.complianceToday = 'Tell us how compliance is handled today.';

  if (!TIMINGS.includes(timing as (typeof TIMINGS)[number]))
    errors.timing = 'Select your pilot timing.';

  if (notes.length > MAX.notes) errors.notes = 'Notes are too long.';

  if (!consent) errors.consent = 'Consent is required so we can respond to you.';

  if (Object.keys(errors).length > 0) return { ok: false, errors, honeypot };
  if (honeypot) return { ok: false, errors: {}, honeypot: true };

  return {
    ok: true,
    honeypot: false,
    value: {
      fullName,
      workEmail,
      organization,
      role,
      entityScale,
      jurisdictions,
      complianceToday,
      timing,
      ...(notes ? { notes } : {}),
      consent: true,
    },
  };
}

export const GENERIC_SUCCESS =
  'Thank you. The Sentience team will review your compliance footprint and follow up by email.';

export const GENERIC_FAILURE =
  'We could not submit your request right now. Please try again shortly, or email us directly.';
